import { CustomerOrderBase } from '../../../contracts/customer-order.js';
import { Result } from '../../../utility/result.js';
import { ErrorLike } from '../../core/error-like.js';
import { OrderSideEffectContext } from './order-side-effect-context.js';
import { IOrderSideEffectProcessor, IOrderSideEffectResult } from './order-side-effect-processor.js';

export interface OrderSideEffectRunnerEntry {
  processor: IOrderSideEffectProcessor,
  priority: number,
  critical: boolean,
}

export class OrderSideEffectRunner {
  private readonly processors: OrderSideEffectRunnerEntry[];

  constructor(args: {
      processors?: { processor: IOrderSideEffectProcessor, priority?: number, critical?: boolean }[],
  }) {
    this.processors = [];

    if(args.processors != null) {
      for(const processor of args.processors) {
        this.processors.push({
          processor: processor.processor,
          priority: processor.priority ?? 0,
          critical: processor.critical ?? false,
        });
      }
      this.processors.sort((a, b) => a.priority - b.priority);
    }
  }

  addProcessor(processor: IOrderSideEffectProcessor, opts?: { priority?: number, critical?: boolean }): void {
    const priority = opts?.priority ?? 0;
    const critical = opts?.critical ?? false;

    for(let i = 0; i < this.processors.length; i++) {
      // use insert sort to maintain order based on priority
      if(priority < this.processors[i].priority) {
        this.processors.splice(i, 0, { processor, priority, critical });
        return;
      }
    }

    this.processors.push({ processor, priority, critical });
  }

  async run(order: CustomerOrderBase): Promise<Result<OrderSideEffectContext, ErrorLike>> {
    const context = new OrderSideEffectContext(order);

    for(const entry of this.processors) {
      const result = await entry.processor.execute(order);
      if(result.isError && entry.critical) {
        return Result.error(result.error);
      }
      else if(result.isError) {
        continue;
      }

      if(result.value.$key !== IOrderSideEffectResult.KEY_EMPTY) {
        context.setData(result.value.$key, result.value);
      }
    }

    return Result.ok(context);
  }
}
